export const NOTIFICATIONS_QUERY_KEYS = {
  notifications: 'notifications',
};

export const notificationRoleScope = {
  USER: 'User',
  ENTITY_MEMBER: 'EntityMember',
  GAMMA_ADMIN: 'GammaAdmin',
} as const;
export type NotificationRoleScope =
  (typeof notificationRoleScope)[keyof typeof notificationRoleScope];

export type FollowUpDigestBook = {
  ownerType: 'User' | 'Entity' | 'Gamma';
  ownerId: string;
  localDate: string;
  contactCount: number;
};

export interface INotification {
  id: string;
  title: string;
  message: string;
  read: boolean;
  priority: NotificationPriorityType;
  type: NotificationType;
  roleScope: NotificationRoleScope;
  entityId: string | null;
  metadata: {
    entityName?: string;
    entityId?: string;
    fundName?: string;
    fundId?: string;
    companyId?: string;
    companyName?: string;
    offerId?: string;
    /** Follow-up digest: how many contacts are due, and the local day they were due on. */
    contactCount?: number;
    localDate?: string;
    books?: FollowUpDigestBook[];
  };
  createdAt: string;
}

export interface INotificationServer extends INotification {
  type: ServerNotificationType;
}
export interface INotificationClient extends INotification {
  type: ClientNotificationType;
}

export interface GetNotificationParams {
  limit?: number;
  page?: number;
  filter?: NotificationFilterType;
  search?: string;
}

export const notificationFilter = {
  NEW: 'new',
  ALL: 'all',
  READ: 'read',
} as const;
export type NotificationFilterType = (typeof notificationFilter)[keyof typeof notificationFilter];

export const notificationPriority = {
  HIGH: 0,
  REGULAR: 1,
} as const;
export type NotificationPriorityType =
  (typeof notificationPriority)[keyof typeof notificationPriority];

export const serverNotificationType = {
  ENTITY_MEMBER_INVITATION: 'EntityMemberInvitation',
  FUND_TEAM_INVITATION: 'FundTeamInvitation',
  GAMMA_TEAM_INVITATION: 'GammaTeamInvitation',
  LOGIN_FROM_NEW_DEVICE: 'LoginFromNewDevice',
  USER_PASSWORD_RESET_REQUESTED: 'UserPasswordResetRequested',
  TEST_NOTIFICATION: 'TestNotification',
  PROFILE_INCOMPLETE: 'ProfileIncomplete',
  FOLLOWED_COMPANY_NEW_OFFER: 'FollowedCompanyNewOffer',
  ENTITY_APPROVED: 'EntityApproved',
  /** Event check-in alert — GammaAdmin-scoped, raised by the events context. */
  EVENT_CHECK_IN: 'EventCheckIn',
  EVENT_TICKET_REQUEST_APPROVED: 'EventTicketRequestApproved',
  EVENT_TICKET_REQUEST_DECLINED: 'EventTicketRequestDeclined',
  /** Daily grouped reminder digest — one per user per local day (GP-4203). */
  CONTACT_FOLLOW_UP_DUE: 'ContactFollowUpDue',
} as const;
export type ServerNotificationType =
  (typeof serverNotificationType)[keyof typeof serverNotificationType];

export const clientNotificationType = {
  QUESTIONNAIRE: 'Questionnaire',
  KYC: 'KYC',
  PROFILE: 'FillOutYourProfile',
} as const;
export type ClientNotificationType =
  (typeof clientNotificationType)[keyof typeof clientNotificationType];

export type NotificationType = ServerNotificationType | ClientNotificationType;

export interface NotificationsBatchReadBody {
  notificationIds: string[];
}

export interface ReadAllNotificationsResponse {
  total: number;
  new: number;
  markedCount: number;
}

export const isClientNotificationType = (type: NotificationType): type is ClientNotificationType =>
  Object.values(clientNotificationType).includes(type as ClientNotificationType);
